import { useEffect, useRef, useState, type ReactNode } from "react";
import { Btn, Checkbox, HeroTile, Icon, MeterBar, Pill, StatusPill, T, alpha } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";
import { RowActions } from "../../shell/composites";
import { tabExport } from "../../shell/list-composites";
import { eventActions } from "./eventActions";
import { CancelEventConfirm } from "./CancelEventConfirm";
import { SHARE_SOURCES } from "../../lib/public-url";
import type { UIEvent } from "../../lib/events-adapter";
// Row-menu Publish for a draft (CC, 2026-10-06; separate hunk for the parent to merge).
import { publishEvent } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { refetchEvents } from "./useEvents";

/** Payments (2026-08-04) — minor-units → display money for the Revenue column
 * and the Paid pill. Symbol from the row currency; defaults to £. Presentational
 * only (the UI never computes an amount — it formats what the adapter carries). */
function fmtMoney(minor?: number, currency?: string): string {
  if (minor == null) return "—";
  const sym = currency === "USD" ? "$" : currency === "EUR" ? "€" : "£";
  return `${sym}${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function eventLocationId(ev: UIEvent): string {
  // LOCAL PATCH (platform side, 2026-09-22): the API may carry the legacy
  // camelCase alias, while the generated UIEvent type declares location_id.
  const raw = ev.raw as typeof ev.raw & { locationId?: unknown };
  return String(raw.locationId ?? raw.location_id ?? "").trim();
}

/**
 * The Events status vocabulary — ONE map (2026-08-19, §3). The pill's five-branch
 * ternary here and `STATUS_ORDER` in EventsList were two copies of the same list,
 * free to drift; a status added to one and not the other sorted or painted wrong.
 * Order drives the "Status" sort; tone + label drive the StatusPill.
 */
export const EVENT_STATUS: Record<string, { order: number; tone: React.ComponentProps<typeof StatusPill>["tone"]; labelKey: string; label: string }> = {
  live:     { order: 0, tone: "success", labelKey: "events.status.live",      label: "Live" },
  upcoming: { order: 1, tone: "primary", labelKey: "events.status.upcoming",  label: "Upcoming" },
  draft:    { order: 2, tone: "neutral", labelKey: "events.status.draft",     label: "Draft" },
  canceled: { order: 3, tone: "danger",  labelKey: "events.status.cancelled", label: "Cancelled" },
  past:     { order: 4, tone: "neutral", labelKey: "events.status.ended",     label: "Ended" },
};

/**
 * EventsInlineTable — the Events page table. Migrated (2026-08-06) from a
 * hand-rolled `<table>` + hand-rolled bulk bar to the jrni-ui `DataTable`
 * (typed columns, header sort, row selection, selection toolbar). The cell
 * content — hero tile, Paid/Free pill, capacity bar, host Avatar, status Pill,
 * RowActions — is preserved verbatim; only the table chrome is now a common
 * component. Sort is driven by the parent (EventsList owns the toggle); the
 * DataTable reports which column header was clicked.
 *
 * Approvals column gated off until the approvals data layer ports (flag);
 * bulk Export builds a real CSV of the selected rows (tabExport); bulk Archive
 * is honest-disabled — no events-archive op exists at 93196e63 (REQ-084).
 */
export function EventsInlineTable({ events, sortKey, sortDir, onSort, folderChipsFor, foldersItemFor, onBulkFolderMove }: {
  events: UIEvent[];
  sortKey: string;
  sortDir: "asc" | "desc";
  onSort?: (sortId: string) => void;
  /** Folder Library hooks (2026-08-13) — chips by the title and a Folders… row
   * action, both supplied by EventsList's useFolderLibrary. Optional and
   * no-ops when absent so other callers are unaffected. */
  folderChipsFor?: (id: string) => ReactNode;
  foldersItemFor?: (id: string) => { icon: ReactNode; label: string; onClick: (e?: unknown) => void };
  /** Bulk "Move to folder…" for the TICKED rows (2026-09-18, PP).
   *
   *  The folder shell ships its own purple selection bar carrying this action,
   *  but it reads `lib.sel` — and this table owns its selection privately in
   *  `DataTable`, so that bar could never appear here and ticking rows offered
   *  no way to file them. Rather than run two selection models, the action
   *  joins the toolbar the ticks already raise, and the caller syncs the ids
   *  into the shell before opening its picker. */
  onBulkFolderMove?: (ids: string[], at: { x: number; y: number }) => void;
}) {
  const nav = useNav();
  const t = useT();
    const paymentsOn = events.some((e) => e.isPaid != null || e.revenueMinor != null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cancelling, setCancelling] = useState<UIEvent | null>(null); // ui-local — event awaiting the cancel/delete confirm
  /* Fit the pane, never scroll sideways (2026-10-02). Location rides the Event subline;
     below 980px the Host column folds into it too. Measured on the table's own box,
     not the window, because the left rail and preview pane vary. ui-local */
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [boxW, setBoxW] = useState(0);
  const narrow = boxW > 0 && boxW < 980;
  useEffect(() => {
    const el = boxRef.current; if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setBoxW(Math.round(e.contentRect.width)));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  const visibleIds = events.map((e) => e.id);
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.includes(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bulk Export does real work — CSV of the selected rows via tabExport
  // (download-then-toast). No events-archive op exists → Archive honest-disabled.
  const bulkExport = () => {
    const rows = events.filter((e) => selected.has(e.id)).map((e) => ({ title: e.title, status: e.status, date: e.date, time: e.time, location: e.location, registered: e.registered, capacity: e.capacity, revenue: e.revenueMinor != null ? fmtMoney(e.revenueMinor, e.currency) : "" }));
    tabExport("events", rows);
  };

  /* ── Card list (2026-10-02, Tickets "Ticket groups" layout) ───────────────
     One white rounded card, one row per event: title + subline + when on the left,
     the registration bar with its caption in the middle, the status and counts,
     then Edit + ⋯ on the right. Replaces the DataTable — no header row, nothing
     scrolls sideways. Selection and the bulk bar are kept (checkbox per row).
     The middle bar drops under 760px of card width; status drops under 600. ui-local */
  const allOn = events.length > 0 && events.every((e) => selected.has(e.id));
  const toggle = (id: string, on: boolean) => setSelected((cur) => { const n = new Set(cur); if (on) n.add(id); else n.delete(id); return n; }); // ui-local
  const showBar = !narrow || (boxW >= 760);
  const showStatus = boxW === 0 || boxW >= 600;
  /* Row-menu Publish for a draft (CC, 2026-10-06; separate hunk for the parent to merge): the
     same publish the designer's header runs (the event goes live; the worker materialises its
     public page). A refusal names the server's code; the row stays a draft. */
  const publishDraft = (ev: UIEvent) => {
    void publishEvent(ev.id).then(() => {
      void refetchEvents();
      toast({ tone: "success", title: t("events.inlineTable.actions.publishedTitle", "Published"), subtitle: t("events.inlineTable.actions.publishedSub", "“{title}” is on the public site and open for registration.", { title: ev.title }) });
    }, (e: unknown) => {
      const code = (e as { code?: unknown } | null)?.code;
      toast({ tone: "danger", title: t("events.inlineTable.actions.publishFailed", "Couldn't publish"), subtitle: code === "EVENT_APPROVAL_REQUIRED"
        ? t("events.inlineTable.actions.publishNeedsApproval", "This event needs approval before it can be published. Open it with Edit to submit it for approval.")
        : [t("events.inlineTable.actions.publishFailedSub", "The event is still a draft. Open it with Edit to see what it needs."), code ? String(code) : ""].filter(Boolean).join(" ") });
    });
  };
  const moreFor = (ev: UIEvent) => [
    ...(ev.status === "draft" && can("events.write") ? [{ icon: <Icon.globe size={14} />, label: t("events.inlineTable.actions.publish", "Publish"), sub: t("events.inlineTable.actions.publishHint", "Puts it on the public site"), onClick: () => publishDraft(ev) }, "divider" as const] : []),

    /* Preview (2026-08-25) — the designer's own preview of this event's
       journey pages, keyed `event:<id>`. */
    { icon: <Icon.eye size={14} />, label: t("events.inlineTable.actions.preview", "Preview"), onClick: () => nav.go("newjourneys", { journey: `event:${ev.id}`, preview: "1" }) },
    { icon: <Icon.copy size={14} />, label: t("events.detailOverlay.duplicate", "Duplicate"), onClick: () => eventActions.duplicateEvent(ev, nav) },
    "divider" as const,
    { icon: <Icon.settings size={14} />, label: t("events.inlineTable.actions.manageEvent", "Manage event"), sub: t("events.inlineTable.actions.registrationsCheckInDetails", "Registrations, check-in & details"), onClick: () => nav.openEvent(ev) },
    { icon: <Icon.external size={14} />, label: t("events.inlineTable.actions.openPublicPage", "Open public page"), onClick: () => eventActions.openLandingPage(ev, nav) },
    ...(can("locations.read") && eventLocationId(ev) ? [{ icon: <Icon.pin size={14} />, label: t("events.inlineTable.actions.viewLocation", "View location"), onClick: () => nav.go("locations.edit", { id: eventLocationId(ev), view: 1 }) }] : []),
    { icon: <Icon.link size={14} />, label: t("events.inlineTable.actions.copyPublicUrl", "Copy public URL"), onClick: () => eventActions.copyPublicUrl(ev, SHARE_SOURCES.eventsTable) },
    "divider" as const,
    ...(foldersItemFor ? [foldersItemFor(ev.id)] : []),
    { icon: <Icon.layers size={14} />, label: t("events.inlineTable.actions.saveAsTemplate", "Save as template"), onClick: () => eventActions.saveAsTemplate(ev, nav) },
    { icon: <Icon.download size={14} />, label: t("events.inlineTable.actions.exportGuestsCsv", "Export guests (CSV)"), onClick: () => eventActions.exportGuestList(ev) },
    ...(ev.status === "canceled" ? [] : ["divider" as const]),
    ...(ev.status === "canceled" ? [] : [ev.status === "draft"
      ? { icon: <Icon.trash size={14} />, label: t("events.inlineTable.actions.deleteDraft", "Delete draft"), danger: true, onClick: () => setCancelling(ev) }
      : { icon: <Icon.x size={14} />, label: t("events.inlineTable.actions.cancelEvent", "Cancel event"), danger: true, sub: t("events.inlineTable.actions.notifiesRegisteredGuests", "Notifies registered guests"), onClick: () => setCancelling(ev) }]),
  ];

  return (
    <div ref={boxRef} style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
      {selected.size > 0 ? (
        <div role="toolbar" aria-label={t("events.inlineTable.selected", "selected")} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: T.p3, border: `1px solid ${T.p4}`, borderRadius: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.p1, marginRight: 4 }}>{selected.size} {t("events.inlineTable.selected", "selected")}</span>
          {onBulkFolderMove && can("events.write") ? <Btn variant="ghost" size="sm" icon={<Icon.folder size={12} />} onClick={(e) => onBulkFolderMove(Array.from(selected), { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY })}>{t("events.inlineTable.bulk.moveToFolder", "Move to folder…")}</Btn> : null}
          {can("reports.export") ? <Btn variant="ghost" size="sm" icon={<Icon.csv size={12} />} onClick={bulkExport}>{t("events.inlineTable.bulk.export", "Export")}</Btn> : null}
          {can("events.write") ? <Btn variant="ghost" size="sm" icon={<Icon.archive size={12} />} disabled title={t("events.inlineTable.bulk.archiveUnavailable", "Not available yet — no events-archive operation is declared in the wiring kit (REQ-084).")}>{t("events.inlineTable.bulk.archive", "Archive")}</Btn> : null}
          <span style={{ flex: 1 }} />
          <Btn variant="ghost" size="sm" onClick={() => setSelected(new Set())}>{t("common.actions.clear", "Clear")}</Btn>
        </div>
      ) : null}
      <CancelEventConfirm ev={cancelling} nav={nav} onClose={() => setCancelling(null)} />
      <div role="list" style={{ background: T.white, border: `1px solid ${T.n5}`, borderRadius: 14, overflow: "hidden" }}>
        {events.length > 1 ? (
          <div style={{ display: "flex", alignItems: "center", padding: "10px 18px", borderBottom: `1px solid ${T.n5}`, fontSize: 12.5, color: T.soft }}>
            <Checkbox checked={allOn ? true : selected.size ? "mixed" : false} onChange={(on: boolean) => setSelected(on ? new Set(events.map((e) => e.id)) : new Set())} label={t("events.inlineTable.selectAll", "Select all")} />
          </div>
        ) : null}
        {events.map((ev, k) => {
          const st = EVENT_STATUS[ev.status] ?? EVENT_STATUS.upcoming;
          const sub = [ev.template, ev.location].filter(Boolean).join(" · ");
          const when = [ev.date, ev.time && ev.zone ? `${ev.time} ${ev.zone}` : ev.time].filter(Boolean).join(" · ");
          return (
            <div key={ev.id} role="listitem" data-id={ev.id} onClick={() => nav.openEvent(ev)}
              style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 18px", borderTop: k === 0 && events.length <= 1 ? "none" : k === 0 ? "none" : `1px solid ${T.n5}`, cursor: "pointer", background: selected.has(ev.id) ? alpha(T.p1, 4) : T.white }}
>
              <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
                <Checkbox checked={selected.has(ev.id)} onChange={(on: boolean) => toggle(ev.id, on)} aria-label={ev.title} />
              </span>
              <HeroTile src={ev.heroImage ?? undefined} initials={ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()} hue={ev.hue} size={40} radius={8} />
              <div style={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span title={ev.title} style={{ fontSize: 15, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{ev.title}</span>
                  {ev.isPaid === true ? <Pill tone="primary">{ev.priceFromMinor != null ? t("events.inlineTable.text", "{arg1} · {arg2}", { arg1: t("events.inlineTable.paid", "Paid"), arg2: fmtMoney(ev.priceFromMinor, ev.currency) }) : t("events.inlineTable.paid", "Paid")}</Pill> : null}
                  {folderChipsFor ? <span style={{ flexShrink: 0, display: "inline-flex" }}>{folderChipsFor(ev.id)}</span> : null}
                </div>
                {sub ? <span style={{ fontSize: 13, color: T.n2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</span> : null}
                <span style={{ fontSize: 12.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{when}{ev.host ? ` · ${ev.host}` : ""}</span>
              </div>
              {showBar ? (
                <div style={{ flex: "0 1 220px", minWidth: 140, display: "flex", flexDirection: "column", gap: 6 }}>
                  {ev.capacity > 0
                    ? <MeterBar value={ev.registered} max={ev.capacity} size="sm" {...(ev.status === "live" ? { tone: "success" as const } : null)} aria-label={t("events.inlineTable.headers.registered", "Registered")} />
                    : <div style={{ height: 6, borderRadius: 999, background: T.n5 }} />}
                  <span style={{ fontSize: 12.5, color: T.soft }}>
                    <b style={{ color: T.n1 }}>{ev.registered}</b>{ev.capacity > 0 ? ` / ${ev.capacity}` : " / ∞"} {t("events.inlineTable.registeredLower", "registered")}
                    {paymentsOn && ev.isPaid ? <> · <b style={{ color: T.n1 }}>{fmtMoney(ev.revenueMinor, ev.currency)}</b></> : null}
                  </span>
                </div>
              ) : null}
              {showStatus ? <span style={{ flex: "0 0 auto", width: 112, display: "inline-flex" }}><StatusPill tone={st.tone} dot>{t(st.labelKey, st.label)}</StatusPill></span> : null}
              <div onClick={(e) => e.stopPropagation()} style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6 }}>
                {can("events.write") ? <Btn variant="secondary" size="sm" onClick={() => eventActions.editEvent(ev, nav)}>{t("common.actions.edit", "Edit")}</Btn> : null}
                <RowActions audit={{ type: "event", id: ev.id, name: ev.title }} moreHeader={ev.title} moreItems={moreFor(ev)} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
